import { useEffect, useMemo, useState } from "react";
import { api, fmtNum, getToken, API } from "@/lib/api";
import { Card } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Activity as ActivityIcon,
  Plus,
  Pencil,
  Trash2,
  Upload as UploadIcon,
  Search,
  ChevronLeft,
  ChevronRight,
  X,
  Download,
} from "lucide-react";
import { toast } from "sonner";

const STYLES = {
  "policy:create": { icon: Plus, color: "text-emerald-700", bg: "bg-emerald-100", ring: "ring-emerald-200", label: "Policy created" },
  "policy:update": { icon: Pencil, color: "text-blue-700", bg: "bg-blue-100", ring: "ring-blue-200", label: "Policy updated" },
  "policy:delete": { icon: Trash2, color: "text-rose-700", bg: "bg-rose-100", ring: "ring-rose-200", label: "Policy deleted" },
  "journal_entry:create": { icon: Plus, color: "text-indigo-700", bg: "bg-indigo-100", ring: "ring-indigo-200", label: "JE posted" },
  "journal_entry:update": { icon: Pencil, color: "text-indigo-700", bg: "bg-indigo-100", ring: "ring-indigo-200", label: "JE edited" },
  "journal_entry:delete": { icon: Trash2, color: "text-rose-700", bg: "bg-rose-100", ring: "ring-rose-200", label: "JE deleted" },
  "upload:upload": { icon: UploadIcon, color: "text-amber-700", bg: "bg-amber-100", ring: "ring-amber-200", label: "File uploaded" },
};

const ENTITY_LABELS = {
  policy: "Policies",
  journal_entry: "Journal Entries",
  upload: "Uploads",
};
const ACTION_LABELS = {
  create: "Created",
  update: "Updated",
  delete: "Deleted",
  upload: "Uploaded",
};

function relTime(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = (Date.now() - d.getTime()) / 1000;
  if (diff < 60) return "just now";
  if (diff < 3600) return `${Math.floor(diff / 60)}m ago`;
  if (diff < 86400) return `${Math.floor(diff / 3600)}h ago`;
  if (diff < 86400 * 7) return `${Math.floor(diff / 86400)}d ago`;
  return d.toLocaleDateString("en-US", { month: "short", day: "2-digit", year: "numeric" });
}

function fullTime(iso) {
  if (!iso) return "";
  try {
    return new Date(iso).toLocaleString("en-US", {
      month: "short",
      day: "2-digit",
      year: "numeric",
      hour: "2-digit",
      minute: "2-digit",
    });
  } catch {
    return iso;
  }
}

// group items by day
function groupByDay(items) {
  const groups = new Map();
  for (const it of items) {
    const day = (it.at || "").slice(0, 10);
    if (!groups.has(day)) groups.set(day, []);
    groups.get(day).push(it);
  }
  return Array.from(groups.entries());
}

function formatDayHeader(day) {
  if (!day) return "Unknown";
  const d = new Date(day + "T00:00:00");
  const today = new Date();
  const diff = Math.round((today - d) / 86400000);
  if (diff === 0) return "Today";
  if (diff === 1) return "Yesterday";
  if (diff < 7) return d.toLocaleDateString("en-US", { weekday: "long" });
  return d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "2-digit", year: "numeric" });
}

export default function Activity() {
  const [page, setPage] = useState(1);
  const [pageSize] = useState(50);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(false);
  const [facets, setFacets] = useState({ users: [], entities: [], actions: [] });

  const [entity, setEntity] = useState("__all__");
  const [action, setAction] = useState("__all__");
  const [userEmail, setUserEmail] = useState("__all__");
  const [search, setSearch] = useState("");
  const [since, setSince] = useState("");
  const [until, setUntil] = useState("");

  useEffect(() => {
    api.get("/activity/facets").then((r) => setFacets(r.data)).catch(() => {});
  }, []);

  const params = useMemo(() => {
    const p = { page, page_size: pageSize };
    if (entity !== "__all__") p.entity = entity;
    if (action !== "__all__") p.action = action;
    if (userEmail !== "__all__") p.user_email = userEmail;
    if (search.trim()) p.search = search.trim();
    if (since) p.since = `${since}T00:00:00+00:00`;
    if (until) p.until = `${until}T23:59:59+00:00`;
    return p;
  }, [page, pageSize, entity, action, userEmail, search, since, until]);

  useEffect(() => {
    setLoading(true);
    api
      .get("/activity", { params })
      .then((r) => {
        setItems(r.data.items);
        setTotal(r.data.total);
      })
      .finally(() => setLoading(false));
  }, [params]);

  useEffect(() => {
    setPage(1);
  }, [entity, action, userEmail, search, since, until]);

  function clearAll() {
    setEntity("__all__");
    setAction("__all__");
    setUserEmail("__all__");
    setSearch("");
    setSince("");
    setUntil("");
  }

  async function exportCsv() {
    const qs = new URLSearchParams();
    if (entity !== "__all__") qs.set("entity", entity);
    if (action !== "__all__") qs.set("action", action);
    if (userEmail !== "__all__") qs.set("user_email", userEmail);
    if (search.trim()) qs.set("search", search.trim());
    if (since) qs.set("since", `${since}T00:00:00+00:00`);
    if (until) qs.set("until", `${until}T23:59:59+00:00`);
    const t = getToken();
    try {
      const r = await fetch(`${API}/activity/export.csv?${qs.toString()}`, {
        headers: { Authorization: `Bearer ${t}` },
      });
      if (!r.ok) throw new Error("Export failed");
      const rows = r.headers.get("X-Row-Count");
      const blob = await r.blob();
      const url = window.URL.createObjectURL(blob);
      const a = document.createElement("a");
      a.href = url;
      a.download = `mga_activity_${new Date().toISOString().slice(0, 10)}.csv`;
      a.click();
      window.URL.revokeObjectURL(url);
      toast.success(`Exported ${fmtNum(Number(rows) || 0)} events`);
    } catch (e) {
      toast.error(e.message);
    }
  }

  const totalPages = Math.max(1, Math.ceil(total / pageSize));
  const activeCount =
    (entity !== "__all__" ? 1 : 0) +
    (action !== "__all__" ? 1 : 0) +
    (userEmail !== "__all__" ? 1 : 0) +
    (search.trim() ? 1 : 0) +
    (since ? 1 : 0) +
    (until ? 1 : 0);

  const grouped = useMemo(() => groupByDay(items), [items]);

  return (
    <div className="space-y-4 max-w-[1200px]" data-testid="activity-page">
      {/* Header card */}
      <Card className="p-6 border-slate-200">
        <div className="flex items-start gap-4">
          <div className="w-11 h-11 rounded-xl bg-blue-50 flex items-center justify-center ring-1 ring-blue-100">
            <ActivityIcon className="w-5 h-5 text-blue-600" strokeWidth={1.75} />
          </div>
          <div className="flex-1">
            <h2 className="text-lg font-semibold tracking-tight">Activity log</h2>
            <p className="text-sm text-slate-500 mt-0.5">
              Every create, update, delete & upload across the workspace. Use the filters to audit a person,
              a day, or a specific entity.
            </p>
          </div>
          <div className="text-right">
            <div className="text-[11px] uppercase tracking-wider text-slate-400 font-medium">
              Events recorded
            </div>
            <div className="text-2xl font-semibold tabular-nums tracking-tight">{fmtNum(total)}</div>
          </div>
        </div>
      </Card>

      {/* Filters */}
      <Card className="p-4 border-slate-200">
        <div className="flex flex-wrap items-end gap-3">
          <div className="flex-1 min-w-[240px]">
            <Label className="text-[11px] uppercase tracking-wider text-slate-400">Search</Label>
            <div className="relative mt-1">
              <Search className="absolute left-3 top-1/2 -translate-y-1/2 w-4 h-4 text-slate-400" />
              <Input
                data-testid="activity-search"
                value={search}
                onChange={(e) => setSearch(e.target.value)}
                placeholder="Title, user, email…"
                className="pl-9"
              />
            </div>
          </div>
          <div className="min-w-[160px]">
            <Label className="text-[11px] uppercase tracking-wider text-slate-400">Entity</Label>
            <Select value={entity} onValueChange={setEntity}>
              <SelectTrigger className="mt-1 h-9" data-testid="activity-entity">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                {facets.entities.map((e) => (
                  <SelectItem key={e} value={e}>{ENTITY_LABELS[e] || e}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[140px]">
            <Label className="text-[11px] uppercase tracking-wider text-slate-400">Action</Label>
            <Select value={action} onValueChange={setAction}>
              <SelectTrigger className="mt-1 h-9" data-testid="activity-action">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                {facets.actions.map((a) => (
                  <SelectItem key={a} value={a}>{ACTION_LABELS[a] || a}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div className="min-w-[200px]">
            <Label className="text-[11px] uppercase tracking-wider text-slate-400">User</Label>
            <Select value={userEmail} onValueChange={setUserEmail}>
              <SelectTrigger className="mt-1 h-9" data-testid="activity-user">
                <SelectValue />
              </SelectTrigger>
              <SelectContent>
                <SelectItem value="__all__">All</SelectItem>
                {facets.users.map((u) => (
                  <SelectItem key={u} value={u}>{u}</SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-400">From</Label>
            <Input
              data-testid="activity-since"
              type="date"
              className="mt-1 h-9 w-[150px]"
              value={since}
              onChange={(e) => setSince(e.target.value)}
            />
          </div>
          <div>
            <Label className="text-[11px] uppercase tracking-wider text-slate-400">To</Label>
            <Input
              data-testid="activity-until"
              type="date"
              className="mt-1 h-9 w-[150px]"
              value={until}
              onChange={(e) => setUntil(e.target.value)}
            />
          </div>
          <Button
            variant="outline"
            onClick={clearAll}
            disabled={activeCount === 0}
            data-testid="activity-clear"
            className="h-9"
          >
            <X className="w-4 h-4 mr-1" /> Clear
          </Button>
          <Button
            onClick={exportCsv}
            data-testid="activity-export"
            disabled={total === 0}
            className="h-9 bg-blue-600 hover:bg-blue-700"
          >
            <Download className="w-4 h-4 mr-1" /> Export CSV
          </Button>
        </div>
      </Card>

      {/* Timeline */}
      <Card className="border-slate-200 overflow-hidden">
        <div className="px-5 py-3 border-b border-slate-200 bg-slate-50/50 flex items-center justify-between">
          <div className="text-[12px] text-slate-600">
            <span className="font-medium text-slate-900">{fmtNum(total)}</span> events
            {activeCount > 0 && (
              <span className="ml-2 text-blue-600">({activeCount} filter{activeCount > 1 ? "s" : ""})</span>
            )}
          </div>
          <div className="flex items-center gap-2 text-[12px] text-slate-500">
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              disabled={page === 1}
              onClick={() => setPage(page - 1)}
              data-testid="activity-page-prev"
            >
              <ChevronLeft className="w-4 h-4" />
            </Button>
            <span>
              Page <b>{page}</b> / {totalPages}
            </span>
            <Button
              size="icon"
              variant="ghost"
              className="h-7 w-7"
              disabled={page >= totalPages}
              onClick={() => setPage(page + 1)}
              data-testid="activity-page-next"
            >
              <ChevronRight className="w-4 h-4" />
            </Button>
          </div>
        </div>

        <div className="p-6">
          {loading && (
            <div className="py-16 text-center text-slate-400 text-sm">Loading…</div>
          )}
          {!loading && items.length === 0 && (
            <div className="py-16 text-center" data-testid="activity-empty">
              <ActivityIcon className="w-10 h-10 mx-auto text-slate-300" strokeWidth={1.5} />
              <div className="text-sm text-slate-500 mt-3">No activity matches the filters.</div>
              {activeCount > 0 && (
                <Button variant="link" onClick={clearAll} className="text-blue-600">
                  Clear filters
                </Button>
              )}
            </div>
          )}

          <div className="space-y-8">
            {!loading &&
              grouped.map(([day, rows]) => (
                <section key={day}>
                  <div className="flex items-baseline gap-3 mb-3">
                    <h3 className="text-[13px] font-semibold tracking-tight text-slate-900">
                      {formatDayHeader(day)}
                    </h3>
                    <span className="text-[11px] text-slate-400">{rows.length} event{rows.length !== 1 ? "s" : ""}</span>
                  </div>
                  <ol className="space-y-4">
                    {rows.map((a, i) => {
                      const key = `${a.entity}:${a.action}`;
                      const s = STYLES[key] || {
                        icon: ActivityIcon,
                        color: "text-slate-700",
                        bg: "bg-slate-100",
                        ring: "ring-slate-200",
                        label: key,
                      };
                      const Icon = s.icon;
                      const isLast = i === rows.length - 1;
                      return (
                        <li key={`${day}-${i}`} className="flex gap-3" data-testid={`activity-row-${day}-${i}`}>
                          <div className="flex flex-col items-center shrink-0 w-9">
                            <div
                              className={`w-9 h-9 rounded-full ${s.bg} ring-1 ${s.ring} flex items-center justify-center`}
                            >
                              <Icon className={`w-4 h-4 ${s.color}`} strokeWidth={2.25} />
                            </div>
                            {!isLast && <div className="w-px flex-1 bg-slate-200 mt-1.5 mb-[-4px]" />}
                          </div>
                          <div className="flex-1 min-w-0 pb-2">
                            <div className="flex items-baseline justify-between gap-2">
                              <div className="text-sm font-medium text-slate-900 truncate" title={a.title}>
                                {a.title}
                              </div>
                              <time
                                className="text-[11px] text-slate-400 whitespace-nowrap shrink-0"
                                title={fullTime(a.at)}
                              >
                                {relTime(a.at)}
                              </time>
                            </div>
                            <div className="mt-1 flex items-center gap-2 text-[12px] text-slate-500 flex-wrap">
                              <span className={`inline-flex items-center px-1.5 py-0.5 rounded ${s.bg} ${s.color} font-medium text-[11px]`}>
                                {s.label}
                              </span>
                              <span className="text-slate-600">{a.user_name || a.user_email}</span>
                              <span className="text-slate-300">•</span>
                              <span className="text-slate-400">{fullTime(a.at)}</span>
                            </div>
                            {a.meta?.fields?.length > 0 && (
                              <div className="mt-1.5 flex flex-wrap gap-1">
                                {a.meta.fields.slice(0, 8).map((f) => (
                                  <span
                                    key={f}
                                    className="text-[11px] bg-slate-100 text-slate-700 px-1.5 py-0.5 rounded font-mono"
                                  >
                                    {f}
                                  </span>
                                ))}
                                {a.meta.fields.length > 8 && (
                                  <span className="text-[11px] text-slate-400">+{a.meta.fields.length - 8}</span>
                                )}
                              </div>
                            )}
                            {a.entity === "upload" && (
                              <div className="mt-1.5 text-[12px] text-slate-600 bg-amber-50 border border-amber-100 rounded-md px-3 py-1.5 inline-block">
                                <span className="font-medium">{a.meta?.filename || "file"}</span> •
                                <span className="ml-1 text-emerald-700">+{fmtNum(a.meta?.inserted ?? 0)} new</span> •
                                <span className="ml-1 text-blue-700">{fmtNum(a.meta?.updated ?? 0)} updated</span>
                                {a.meta?.total_collections != null && (
                                  <> • <span className="text-slate-500">{a.meta.total_collections} tabs</span></>
                                )}
                              </div>
                            )}
                            {a.entity === "journal_entry" && a.meta && (a.meta.debit != null || a.meta.credit != null) && (
                              <div className="mt-1.5 text-[12px] text-slate-600">
                                <span className="text-slate-400">Debit</span>{" "}
                                <span className="tabular-nums">${fmtNum(a.meta.debit || 0)}</span>
                                <span className="mx-2 text-slate-300">•</span>
                                <span className="text-slate-400">Credit</span>{" "}
                                <span className="tabular-nums">${fmtNum(a.meta.credit || 0)}</span>
                              </div>
                            )}
                          </div>
                        </li>
                      );
                    })}
                  </ol>
                </section>
              ))}
          </div>
        </div>
      </Card>
    </div>
  );
}
